import {
  DashboardCreateDto,
  DashboardDtoResponse,
  DashboardInstanceListDto,
  DashboardListDto,
} from '@asset-metrix/portal-gateway-client-js';
import { QueryClient, QueryFunction, QueryKey } from '@tanstack/react-query';

import {
  DashboardUpdateRequest,
  HydratedDashboard,
} from '../../components/customizableDashboard/utils/dashboard.types';
import {
  prependRoleId,
  QueryKeyWithRole,
  unwrapQueryKeyWithRole,
} from '../../utils/prependRoleId';
import { HttpError } from '../errors';
import { portalGatewayClient } from '../portalGatewayClient';

export function pageDashboardsQueryKey(sourcePageId: string) {
  return prependRoleId(['pageDashboards', sourcePageId]);
}

export function dashboardInstancesQueryKey(sourcePageId: string) {
  return prependRoleId(['dashboardInstances', sourcePageId]);
}

export function dashboardQueryKey(dashboardId: string) {
  return prependRoleId(['dashboard', dashboardId]);
}

export type DashboardQueryKey = ReturnType<typeof dashboardQueryKey>;

export function invalidatePageDashboardsQuery(
  queryClient: QueryClient,
  sourcePageId: string
) {
  return queryClient.invalidateQueries({
    queryKey: pageDashboardsQueryKey(sourcePageId),
  });
}

export function invalidateDashboardInstancesQuery(
  queryClient: QueryClient,
  sourcePageId: string
) {
  return queryClient.invalidateQueries({
    queryKey: dashboardInstancesQueryKey(sourcePageId),
  });
}

export function invalidateDashboardQuery(
  queryClient: QueryClient,
  dashboardId: string
) {
  return queryClient.invalidateQueries({
    queryKey: dashboardQueryKey(dashboardId),
  });
}

export function invalidateDashboardListQueries(
  queryClient: QueryClient,
  sourcePageId: string
) {
  return Promise.all([
    invalidatePageDashboardsQuery(queryClient, sourcePageId),
    invalidateDashboardInstancesQuery(queryClient, sourcePageId),
  ]);
}

export const fetchPageDashboards: QueryFunction<
  DashboardListDto[],
  QueryKeyWithRole<QueryKey>
> = async ({ queryKey }) => {
  const { params } = unwrapQueryKeyWithRole(queryKey);
  const sourcePageId = params[0];
  if (typeof sourcePageId !== 'string' || sourcePageId.length === 0) {
    return [];
  }

  const response = await portalGatewayClient.dashboard.listDashboards({
    sourcePageId,
  });

  return response.data.data;
};

export const fetchDashboard: QueryFunction<
  HydratedDashboard,
  QueryKeyWithRole<QueryKey>
> = async ({ queryKey }) => {
  const { params } = unwrapQueryKeyWithRole(queryKey);
  const dashboardId = params[0];
  if (typeof dashboardId !== 'string' || dashboardId.length === 0) {
    throw new Error('Dashboard id is required.');
  }

  const response =
    await portalGatewayClient.dashboard.getDashboard(dashboardId);

  if (!response.ok) {
    throw new HttpError('Fetching dashboard failed.', response);
  }

  return response.data.data;
};

export async function updateDashboard({
  dashboardId,
  data,
}: {
  dashboardId: string;
  data: DashboardUpdateRequest;
}): Promise<HydratedDashboard> {
  const response = await portalGatewayClient.dashboard.updateDashboard(
    dashboardId,
    data
  );

  if (!response.ok) {
    throw new HttpError('Updating dashboard failed.', response);
  }

  return response.data.data;
}

export async function createDashboard({
  data,
}: {
  data: DashboardCreateDto;
}): Promise<DashboardDtoResponse> {
  const response = await portalGatewayClient.dashboard.createDashboard(data);

  return response.data;
}

/** Thrown when pinning fails and compensating dashboard delete also fails. */
export class CreateDashboardPinFailedError extends Error {
  readonly dashboardId: string;
  readonly pinError: unknown;

  constructor(dashboardId: string, pinError: unknown) {
    super('Dashboard was created but could not be pinned and rollback failed');
    this.name = 'CreateDashboardPinFailedError';
    this.dashboardId = dashboardId;
    this.pinError = pinError;
  }
}

export function isCreateDashboardPinFailedError(
  error: unknown
): error is CreateDashboardPinFailedError {
  return error instanceof CreateDashboardPinFailedError;
}

export async function createDashboardAndPinToNavigation(
  sourcePageId: string,
  values: { name: string; description: string | null }
): Promise<DashboardInstanceListDto> {
  const created = await createDashboard({
    data: {
      sourcePageId,
      name: values.name,
      description: values.description,
    },
  });
  const dashboardId: string = created.data.id;
  try {
    const instances = await fetchDashboardInstances(sourcePageId);
    const nextOrder =
      instances.length === 0
        ? 0
        : Math.max(...instances.map((instance) => instance.order)) + 1;
    return await createDashboardInstance({
      sourcePageId,
      dashboardId,
      order: nextOrder,
    });
  } catch (pinError) {
    try {
      await deleteDashboard({ dashboardId, sourcePageId });
    } catch {
      throw new CreateDashboardPinFailedError(dashboardId, pinError);
    }
    throw pinError;
  }
}

export async function deleteDashboard({
  dashboardId,
  sourcePageId,
}: {
  dashboardId: string;
  sourcePageId: string;
}): Promise<void> {
  await portalGatewayClient.dashboard.deleteDashboard(dashboardId, {
    sourcePageId,
  });
}

export async function fetchDashboardInstances(
  sourcePageId: string
): Promise<DashboardInstanceListDto[]> {
  const response = await portalGatewayClient.dashboard.listDashboardInstances({
    sourcePageId,
  });

  return response.data.data;
}

export async function createDashboardInstance({
  sourcePageId,
  dashboardId,
  order,
}: {
  sourcePageId: string;
  dashboardId: string;
  order: number;
}): Promise<DashboardInstanceListDto> {
  const response = await portalGatewayClient.dashboard.createDashboardInstance(
    { sourcePageId },
    { dashboardId, order }
  );

  return response.data.data;
}

export async function updateDashboardInstance({
  instanceId,
  sourcePageId,
  order,
}: {
  instanceId: string;
  sourcePageId: string;
  order: number;
}): Promise<DashboardInstanceListDto> {
  const response = await portalGatewayClient.dashboard.updateDashboardInstance(
    instanceId,
    { sourcePageId },
    { order }
  );

  return response.data.data;
}

export async function deleteDashboardInstance({
  instanceId,
  sourcePageId,
}: {
  instanceId: string;
  sourcePageId: string;
}): Promise<void> {
  await portalGatewayClient.dashboard.deleteDashboardInstance(instanceId, {
    sourcePageId,
  });
}

export type PersistAllDashboardsModalResult = {
  finalInstancedDashboardIds: string[];
  /** Deletes that completed; callers should drop these from pending draft state. */
  succeededDeleteIds: string[];
  hasFailures: boolean;
  instanceByDashboardId: Map<string, DashboardInstanceListDto>;
};

async function runPersistOp<T>(
  op: () => Promise<T>
): Promise<{ ok: true; value: T } | { ok: false }> {
  try {
    return { ok: true, value: await op() };
  } catch (error) {
    console.error('Failed to persist dashboard modal changes', error);
    return { ok: false };
  }
}

/**
 * Persists All Dashboards modal draft changes via parallel create/delete/update
 * instance calls and staged dashboard deletes.
 */
export async function persistAllDashboardsModalChanges({
  sourcePageId,
  draftInstancedDashboardIds,
  pendingDashboardDeletes,
}: {
  sourcePageId: string;
  draftInstancedDashboardIds: readonly string[];
  pendingDashboardDeletes: ReadonlySet<string>;
}): Promise<PersistAllDashboardsModalResult> {
  const finalInstancedDashboardIds = new Set(draftInstancedDashboardIds);
  const currentInstances = await fetchDashboardInstances(sourcePageId);
  const instanceByDashboardId = new Map(
    currentInstances.map((item) => [item.dashboardId, item])
  );

  const succeededDeleteIds: string[] = [];
  const tasks: Array<() => Promise<boolean>> = [];

  for (const dashboardId of pendingDashboardDeletes) {
    tasks.push(async () => {
      const result = await runPersistOp(() =>
        deleteDashboard({ dashboardId, sourcePageId })
      );
      if (result.ok) {
        succeededDeleteIds.push(dashboardId);
        instanceByDashboardId.delete(dashboardId);
      }
      return result.ok;
    });
  }

  for (const item of currentInstances) {
    if (pendingDashboardDeletes.has(item.dashboardId)) {
      continue;
    }
    if (!finalInstancedDashboardIds.has(item.dashboardId)) {
      tasks.push(async () => {
        const result = await runPersistOp(() =>
          deleteDashboardInstance({
            instanceId: item.id,
            sourcePageId,
          })
        );
        if (result.ok) {
          instanceByDashboardId.delete(item.dashboardId);
        }
        return result.ok;
      });
    }
  }

  for (const [order, dashboardId] of draftInstancedDashboardIds.entries()) {
    const existing = instanceByDashboardId.get(dashboardId);
    if (existing == null) {
      tasks.push(async () => {
        const result = await runPersistOp(() =>
          createDashboardInstance({
            sourcePageId,
            dashboardId,
            order,
          })
        );
        if (result.ok) {
          instanceByDashboardId.set(dashboardId, result.value);
        }
        return result.ok;
      });
      continue;
    }
    if (existing.order !== order) {
      tasks.push(async () => {
        const result = await runPersistOp(() =>
          updateDashboardInstance({
            instanceId: existing.id,
            sourcePageId,
            order,
          })
        );
        if (result.ok) {
          instanceByDashboardId.set(dashboardId, result.value);
        }
        return result.ok;
      });
    }
  }

  const results = await Promise.all(tasks.map((task) => task()));

  return {
    finalInstancedDashboardIds: [...draftInstancedDashboardIds],
    succeededDeleteIds,
    hasFailures: results.some((ok) => !ok),
    instanceByDashboardId,
  };
}
